import compact from 'lodash/compact';
import differenceBy from 'lodash/differenceBy';
import every from 'lodash/every';
import find from 'lodash/find';
import flatMap from 'lodash/flatMap';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import without from 'lodash/without';

import {
  IAutoQuote,
  IAutoThirdPartyDataOrders,
  IDriver,
  IVehicle,
  TExcludedVehicleList,
} from '../../../interfaces';
import {
  getFromSessionStorage,
  setSessionStorage,
} from '../../../services/sessionStorage';
import updateValueAtPath from '../../../services/updateValueAtPath';
import getLienholderObject, { TLienholderValues } from '../getLienholderObject';
import { getVehicleName } from '../getVehicleName';

import getQuoteHealthStatus from './getQuoteHealthStatus';
import handleActionableDataOrderErrors from './handleActionableDataOrderErrors';
import handleDataOrders from './handleDataOrders';
import handleUnrecoverableDataOrderErrors from './handleUnrecoverableDataOrderErrors';

export type TDataType = 'vin_services' | 'clue' | 'mvr';

export type TUpdateLienholderInfo = {
  id: string;
} & TLienholderValues;

export type TUpdateMultipleLienholderInfo = {
  [key: string]: TLienholderValues &
    Pick<IVehicle, 'ownership_status' | 'additional_interest'>;
};

export type TQuoteHealthCheck = {
  quote: IAutoQuote;
  cachedOrders: IAutoThirdPartyDataOrders[];
  cachedDrivers: IDriver[];
  cachedVehicles: IVehicle[];
  analytics?: any;
};

let ineligibleDriverIds: (string | number)[] = JSON.parse(
  getFromSessionStorage('ineligibleDriverIds') || '[]'
);

let ineligibleVehicleIds: string[] = JSON.parse(
  getFromSessionStorage('ineligibleVehicleIds') || '[]'
);

let excludedVehiclesList: TExcludedVehicleList[] = JSON.parse(
  getFromSessionStorage('excludedVehiclesList') || '[]'
);

export function setIneligibleDriverIds(ids: (string | number)[]) {
  if (isArray(ids)) {
    ineligibleDriverIds = ids;
    setSessionStorage('ineligibleDriverIds', JSON.stringify(ids));
  } else if (process.env.REACT_APP_ENV !== 'production') {
    console.error(
      `The value passed to \`setIneligibleDriverIds\` must be an array. Received \`${ids}\`. `
    );
  }
}

export function setIneligibleVehicleIds(ids: string[]) {
  if (isArray(ids)) {
    ineligibleVehicleIds = ids;
    setSessionStorage('ineligibleVehicleIds', JSON.stringify(ids));
  } else if (process.env.REACT_APP_ENV !== 'production') {
    console.error(
      `The value passed to \`setIneligibleVehicleIds\` must be an array. Received \`${ids}\`. `
    );
  }
}

export function setExcludedVehiclesList(vehicles: TExcludedVehicleList[]) {
  if (isArray(vehicles)) {
    excludedVehiclesList = vehicles;
    setSessionStorage('excludedVehiclesList', JSON.stringify(vehicles));
  } else if (process.env.REACT_APP_ENV !== 'production') {
    console.error(
      `The value passed to \`setExcludedVehiclesLit\` must be an array. Received \`${vehicles}\`. `
    );
  }
}

export function clearSessionStorageValues() {
  ineligibleDriverIds = [];
  ineligibleVehicleIds = [];
  excludedVehiclesList = [];
}

export function quoteHealthCheck({
  quote,
  cachedOrders,
  cachedDrivers,
  cachedVehicles,
  analytics,
}: TQuoteHealthCheck) {
  function getOrderChecklist() {
    const vehicles = quote?.application?.vehicles || [];
    const drivers = quote?.application?.drivers || [];

    const newVehiclesForThisHealthCheckRun = differenceBy(
      vehicles,
      cachedVehicles,
      'id'
    );

    const hasUserEditedManuallyAddedVehiclesSinceLastHealthCheck =
      !!cachedVehicles &&
      !!vehicles.filter((vehicle) => {
        if (vehicle.data_source === 'adpf') {
          return false;
        }

        const cachedVehicle = find(cachedVehicles, { id: vehicle.id });
        return cachedVehicle
          ? !every(
              ['vin', 'business_use', 'garage_address', 'rideshare_use'],
              (
                val: 'vin' | 'business_use' | 'garage_address' | 'rideshare_use'
              ) => isEqual(cachedVehicle[val], vehicle[val])
            )
          : true;
      }).length;

    const hasUserEditedManuallyAddedDriversSinceLastHealthCheck =
      !!cachedDrivers &&
      !!drivers.filter((driver) => {
        if (driver.role === 'pni') {
          return false;
        }

        const cachedDriver = find(cachedDrivers, { id: driver.id });
        return cachedDriver ? !isEqual(cachedDriver, driver) : true;
      }).length;

    const requiresVinServices =
      !every(newVehiclesForThisHealthCheckRun, {
        data_source: 'adpf',
      }) || hasUserEditedManuallyAddedVehiclesSinceLastHealthCheck;

    const requiresClue =
      ((!cachedDrivers || !cachedDrivers.length) && drivers.length > 1) ||
      hasUserEditedManuallyAddedDriversSinceLastHealthCheck;

    const requiresMvr = !cachedDrivers || !cachedDrivers.length || requiresClue;

    return compact([
      requiresVinServices && 'vin_services',
      requiresClue && 'clue',
      requiresMvr && 'mvr',
    ]);
  }

  function getAreOrdersComplete(
    quote: IAutoQuote,
    orderChecklist: TDataType[]
  ) {
    const { successfulOrders } = getQuoteHealthStatus(
      quote,
      ineligibleDriverIds,
      ineligibleVehicleIds
    );
    const successfulOrdersFromThisHealthCheckRun = differenceBy(
      successfulOrders,
      cachedOrders,
      (order) => `${order?.data_type}${order?.order_date}`
    );
    const successfulOrdersOnChecklist =
      successfulOrdersFromThisHealthCheckRun.filter((order = {}) =>
        includes(orderChecklist, order.data_type)
      );
    return every(
      orderChecklist,
      (data_type) => !!find(successfulOrdersOnChecklist, { data_type })
    );
  }

  function getMaxLoadingProgress(
    orders: IAutoThirdPartyDataOrders[],
    hasDocRequest: boolean
  ) {
    const isMvrSuccessful = !!find(
      orders,
      (order) => order.data_type === 'mvr' && order.status === 'success'
    );

    if (!isMvrSuccessful) {
      return 85;
    }
    if (!hasDocRequest) {
      return 90;
    }
    return 100;
  }

  function hasOrderError(
    dataType: TDataType,
    errorType: string,
    orders: IAutoThirdPartyDataOrders[]
  ) {
    const orderWithErrorType = find(orders, (order) => {
      if (!errorType) {
        return (
          order.data_type === dataType &&
          includes(['error', 'failure'], order.status)
        );
      }
      return (
        order.data_type === dataType &&
        includes(['error', 'failure'], order.status) &&
        !!find(
          order?.error_details || [],
          (errorDetails = {}) =>
            includes(errorDetails.errors, errorType) &&
            (errorDetails.id || errorDetails.driving_license_number)
        )
      );
    });

    return !!orderWithErrorType;
  }

  function handleGenerateDocsRequest(
    quote: IAutoQuote,
    orderChecklist: TDataType[],
    existingDocRequests: IAutoThirdPartyDataOrders[]
  ) {
    const jobs = quote?.application?.metadata?.job_requests || [];
    const newDocRequests = jobs.filter(
      (job) =>
        !includes(
          (existingDocRequests || []).map(
            (existingDoc) => existingDoc?.order_date
          ),
          job?.order_date
        )
    );
    const hasDocRequest = !!newDocRequests.length;

    const hasPendingRequest = !!find(jobs, {
      status: 'pending',
    });

    if (
      getAreOrdersComplete(quote, orderChecklist) &&
      !hasDocRequest &&
      !hasPendingRequest
    ) {
      setSessionStorage('toggleAutoSignedDocs', '');
      const newJob = { data_type: 'documents', status: 'ready' };
      const updatedQuote = updateValueAtPath({
        base: quote,
        path: 'application.metadata.job_requests',
        updatedValues: [...jobs, newJob],
      });
      return updatedQuote;
    }
    return quote;
  }

  function updateQuoteWithAllOrders(
    quote: IAutoQuote,
    orderChecklist: TDataType[],
    existingDocRequests: IAutoThirdPartyDataOrders[]
  ) {
    const ordersFromThisHealthCheckRun = differenceBy(
      quote?.application?.metadata?.third_party_data_orders,
      cachedOrders,
      (order) => `${order.data_type}${order.order_date}`
    );
    const quoteWithoutOldOrders = updateValueAtPath({
      base: quote,
      path: 'application.metadata.third_party_data_orders',
      updatedValues: ordersFromThisHealthCheckRun,
    });

    return new Promise<IAutoQuote>((resolve) => {
      const updatedQuoteWithoutOldOrders = handleGenerateDocsRequest(
        handleDataOrders(
          handleActionableDataOrderErrors(
            handleUnrecoverableDataOrderErrors(
              quoteWithoutOldOrders,
              analytics
            ),
            ineligibleDriverIds,
            setIneligibleDriverIds,
            setIneligibleVehicleIds,
            analytics
          ),
          orderChecklist,
          ineligibleDriverIds,
          ineligibleVehicleIds
        ),
        orderChecklist,
        existingDocRequests
      );

      const newOrders =
        updatedQuoteWithoutOldOrders?.application?.metadata
          .third_party_data_orders;

      const updatedQuoteWithAllOrders = updateValueAtPath({
        base: updatedQuoteWithoutOldOrders,
        path: 'application.metadata.third_party_data_orders',
        updatedValues: [...cachedOrders, ...newOrders].filter(
          ({ status }) => status !== 'pending'
        ),
      });

      resolve(updatedQuoteWithAllOrders);
    });
  }

  function updateQuoteWithVehicleVins(newVehicleVins: {
    [key: string]: string;
  }) {
    const newVehiclesList = (quote?.application?.vehicles || []).map(
      (vehicle) => {
        if (newVehicleVins[vehicle.id]) {
          return {
            ...vehicle,
            vin: newVehicleVins[vehicle.id],
          };
        }
        return vehicle;
      }
    );

    const updatedQuote = updateValueAtPath({
      base: quote,
      path: 'application.vehicles',
      updatedValues: newVehiclesList,
    });
    return updatedQuote;
  }

  function updateQuoteWithLienholderInfo(newVehicle: TUpdateLienholderInfo) {
    const lienholderObj = getLienholderObject(newVehicle);

    const newVehiclesList = (quote?.application?.vehicles || []).map(
      (vehicle) => {
        if (newVehicle.id === vehicle.id) {
          return {
            ...vehicle,
            lienholder: lienholderObj,
          };
        }
        return vehicle;
      }
    );

    const updatedQuote = updateValueAtPath({
      base: quote,
      path: 'application.vehicles',
      updatedValues: newVehiclesList,
    });
    return updatedQuote;
  }

  function updateQuoteWithMultipleLienholderInfo(
    newLienholders: TUpdateMultipleLienholderInfo
  ) {
    const newVehiclesList = (quote?.application?.vehicles || []).map(
      (vehicle) => {
        if (newLienholders[vehicle.id]) {
          const {
            ownership_status,
            lienholder_name,
            address_line1,
            address_line2,
            address_city,
            address_postal,
            address_region,
            additional_interest,
          } = newLienholders[vehicle.id];

          return {
            ...vehicle,
            lienholder:
              ownership_status !== 'owned'
                ? getLienholderObject({
                    lienholder_name,
                    address_line1,
                    address_line2,
                    address_city,
                    address_region,
                    address_postal,
                  })
                : null,
            ownership_status,
            additional_interest,
          };
        }

        return vehicle;
      }
    );

    const updatedQuote = updateValueAtPath({
      base: quote,
      path: 'application.vehicles',
      updatedValues: newVehiclesList,
    });
    return updatedQuote;
  }

  function updateQuoteWithLicenseInfo(updatedDrivers: IDriver[]) {
    const newDriversList = (quote?.application?.drivers || []).map(
      (driver) => find(updatedDrivers, { id: driver.id }) || driver
    );

    const updatedQuote = updateValueAtPath({
      base: quote,
      path: 'application.drivers',
      updatedValues: newDriversList,
    });
    return updatedQuote;
  }

  function updateQuoteWithExcludedVehicles(excludedVehicles: IVehicle[]) {
    const excludedVehicleIds = excludedVehicles.map((vehicle) => vehicle.id);
    const newExcludedVehiclesList: TExcludedVehicleList[] =
      excludedVehicles.map((vehicle) => ({
        id: vehicle.id,
        title: getVehicleName(vehicle, ['year', 'make', 'model']),
        vin: vehicle?.vin,
      }));
    setIneligibleVehicleIds(
      without(ineligibleVehicleIds, ...excludedVehicleIds)
    );
    setExcludedVehiclesList(
      uniq([...excludedVehiclesList, ...newExcludedVehiclesList])
    );
    const newVehiclesList = (quote?.application?.vehicles || []).filter(
      (vehicle) => !includes(excludedVehicleIds, vehicle.id)
    );

    const updatedQuote = updateValueAtPath({
      base: quote,
      path: 'application.vehicles',
      updatedValues: newVehiclesList,
    });
    return updatedQuote;
  }

  function updateQuoteWithExcludedDrivers(excludedDrivers: IDriver[]) {
    const excludedDriverIds = excludedDrivers.map((driver) => driver.id);
    setIneligibleDriverIds(without(ineligibleDriverIds, ...excludedDriverIds));
    const newDriversList = (quote?.application?.drivers || []).map((driver) =>
      includes(excludedDriverIds, driver.id)
        ? {
            ...driver,
            is_excluded: true,
          }
        : driver
    );
    const updatedQuoteHoldersList = (quote?.application?.holders || []).filter(
      (holder) =>
        !excludedDrivers.some(
          (excludedDriverInfo) =>
            excludedDriverInfo.relationship_to_insured === 'spouse' &&
            holder.position_on_policy === 2
        )
    );
    const updatedQuote = {
      ...quote,
      application: {
        ...quote.application,
        drivers: newDriversList,
        holders: updatedQuoteHoldersList,
      },
    };
    return updatedQuote;
  }

  function getQuoteHealthCheckProps() {
    const vehiclesList = quote?.application?.vehicles || [];
    const noVinVehicleList = vehiclesList.filter((vehicle) => !vehicle.vin);
    const vehicleVinBlacklist = vehiclesList
      .map((vehicle) => vehicle?.vin)
      .filter((vin) => !!vin);

    const driversToExclude = compact(
      ineligibleDriverIds.map((id) =>
        find(
          (quote?.application?.drivers || []).filter(
            (driver) => !driver.is_excluded
          ),
          { id }
        )
      )
    );

    const orders = quote?.application?.metadata?.third_party_data_orders || [];
    const ordersWithVehicleErrors = orders.filter(
      (order) =>
        order.data_type === 'vin_services' &&
        includes(['error', 'failure'], order.status)
    );
    const errorDetails = ordersWithVehicleErrors.map(
      (order) => order.error_details
    );
    const offendingVehicleIds = uniq(
      flatMap(errorDetails, (deets) => deets?.map((deet) => deet.id))
    );

    const vehiclesToExclude = compact(
      offendingVehicleIds.map((id) => find(vehiclesList, { id }))
    );

    const noLienholderVehicleList = vehiclesList.filter(
      (vehicle) =>
        vehicle.ownership_status !== 'owned' &&
        (!vehicle.lienholder ||
          !vehicle.lienholder.address ||
          !vehicle.lienholder.name)
    );

    return {
      vehiclesList,
      noVinVehicleList,
      vehicleVinBlacklist,
      driversToExclude,
      orders,
      ordersWithVehicleErrors,
      errorDetails,
      offendingVehicleIds,
      vehiclesToExclude,
      noLienholderVehicleList,
    };
  }

  return {
    updateQuoteWithAllOrders,
    updateQuoteWithLicenseInfo,
    updateQuoteWithExcludedDrivers,
    updateQuoteWithExcludedVehicles,
    updateQuoteWithLienholderInfo,
    updateQuoteWithVehicleVins,
    updateQuoteWithMultipleLienholderInfo,

    getOrderChecklist,
    getQuoteHealthStatus: (quote: IAutoQuote) =>
      getQuoteHealthStatus(quote, ineligibleDriverIds, ineligibleVehicleIds),
    getAreOrdersComplete,
    getMaxLoadingProgress,
    hasOrderError,
    getQuoteHealthCheckProps,
  };
}
